/**
 * Civilians — the people who actually work in the building. Non-combatants with
 * a role and a daily routine: they sit at their desk, fetch coffee, take a
 * meeting, use the restroom, and go back to work. The stealth challenge is a
 * POPULATED building whose traffic is readable and therefore exploitable — a
 * worker leaving their desk opens a window, a meeting empties a wing.
 *
 * Structurally a `Guard` (so movement, pathfinding and the two-tile render model
 * come for free) but kept in its own array, like the janitor, so no guard
 * behaviour pass — awareness, patrol, dispatch, combat — ever touches them.
 * Their perception is the janitor's model: a short omnidirectional awareness of
 * their surroundings rather than a guard's cone.
 *
 * Population is a function of the building's OPERATING STATE: a day shift fills
 * the office floors; at night the floor is empty but for a lone worker on
 * overtime. Headless — no PixiJS (rule 4).
 */

import { CONFIG } from '../config';
import { isBlinded } from './blind';
import { hasCoverBetween } from './cover';
import { exposure } from './disguise';
import { guardSpeed, moveTowards, spawnGuard, type Guard } from './guard';
import { informGuard } from './janitor';
import { lightLevelAt, visibilityFactor } from './light';
import { syncVisual } from './movement';
import type { Player } from './player';
import { blocksVision } from './tiles';
import { inBounds } from './map';
import { tileAt, type MapData, type Room } from './map';
import { TileType } from './tiles';
import type { World } from './world';

/** What someone does here, which decides where they sit and where they wander. */
export type CivilianRole = 'worker' | 'manager' | 'receptionist' | 'it' | 'clerk';

/** One stop on a routine: a room KIND to be in, and how long to stay. */
export interface Activity {
  /** Room kind the stop happens in ('desk' = their own workstation). */
  where: 'desk' | string;
  /** Seconds spent there once arrived. */
  dwellSec: number;
}

export interface Civilian extends Guard {
  role: CivilianRole;
  /** Their own workstation — where the routine starts and returns to. */
  homeX: number;
  homeY: number;
  /** The schedule loop, and where they are in it. */
  routine: readonly Activity[];
  step: number;
  /** Seconds left at the current stop (counts down once arrived). */
  dwell: number;
  /** Tile they're walking to, or null when settled. */
  targetX: number | null;
  targetY: number | null;
  /** Ticks left to reach the target before giving it up as unreachable. */
  reachTimeout: number;
  /** Saw the intruder — the routine is off, they're running to report it. */
  panicking: boolean;
  /** The guard they're running to, or null when nobody's left to tell. */
  reportGuardId: number | null;
  /** Where they last saw the intruder — the report they carry. */
  sawX: number;
  sawY: number;
  /** Seconds left cowering after reporting (or with nobody to report to). */
  cower: number;
}

const CIVILIAN_FG = 0xd8c48a; // warm parchment — plainly not a guard

/** Which role staffs which room kind, and the glyph each gets. */
const ROOM_ROLE: Record<string, CivilianRole> = {
  'open-office': 'worker',
  'private-office': 'manager',
  meeting: 'worker',
  lobby: 'receptionist',
  server: 'it',
  records: 'clerk',
  storage: 'clerk',
  break: 'worker',
};

/** Rooms that hold a workstation someone is assigned to. Corridors, restrooms,
 *  stairwells and plant rooms are places you pass through, not sit in. */
export const STAFFED_ROOMS: readonly string[] = Object.keys(ROOM_ROLE);

export function roleFor(roomKind: string | undefined): CivilianRole {
  return (roomKind !== undefined ? ROOM_ROLE[roomKind] : undefined) ?? 'worker';
}

/** Spawn one civilian at their workstation. */
export function spawnCivilian(world: World, x: number, y: number, role: CivilianRole): Civilian {
  const c = spawnGuard(world, x, y) as Civilian;
  c.glyph = 'c';
  c.fg = CIVILIAN_FG;
  c.lastSeenGlyph = 'c';
  c.lastSeenFg = CIVILIAN_FG;
  c.isJanitor = true; // reuses the "not a combatant" exclusions (loot, patrol)
  c.role = role;
  c.homeX = x;
  c.homeY = y;
  c.routine = CONFIG.civilian.routines[role];
  c.step = 0;
  c.dwell = 0;
  c.targetX = null;
  c.targetY = null;
  c.reachTimeout = 0;
  c.panicking = false;
  c.reportGuardId = null;
  c.sawX = 0;
  c.sawY = 0;
  c.cower = 0;
  return c;
}

/**
 * Interior floor tiles of `room` someone could stand at. The player's entry tile
 * and the ring around it are excluded: the mission starts with you walking in,
 * and materialising inside the receptionist is not the intended opening.
 */
function freeTiles(map: MapData, room: Room): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  for (let y = room.y0 + 1; y <= room.y1 - 1; y++) {
    for (let x = room.x0 + 1; x <= room.x1 - 1; x++) {
      if (tileAt(map, x, y) !== TileType.Floor) continue;
      const fromSpawn = Math.max(Math.abs(x - map.spawn.x), Math.abs(y - map.spawn.y));
      if (fromSpawn <= CONFIG.civilian.spawnClearance) continue;
      out.push({ x, y });
    }
  }
  return out;
}

/** Is this tile beside a desk/table/counter? People sit AT something. */
function besideFurniture(map: MapData, x: number, y: number): boolean {
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    if (tileAt(map, x + dx, y + dy) === TileType.PropLow) return true;
  }
  return false;
}

/**
 * Staff a floor. Headcount scales with the building's room count and the
 * operating state's `civilianDensity`; each civilian is seated at a workstation
 * — a free tile beside furniture where possible, so people are AT desks rather
 * than standing in the middle of the room.
 *
 * Off-duty floors come back empty except for one worker on overtime, which is
 * exactly the tension the night shift wants: the building is yours, but someone
 * is still in it.
 */
export function populateCivilians(
  world: World,
  map: MapData,
  density: number,
  rng: () => number,
): Civilian[] {
  const rooms = map.rooms.filter((r) => r.kind !== undefined && STAFFED_ROOMS.includes(r.kind));
  if (rooms.length === 0) return [];
  const out: Civilian[] = [];
  const shuffle = <T>(arr: T[]): T[] => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j]!, arr[i]!];
    }
    return arr;
  };

  if (density <= 0) {
    // The lone overtime worker: one person, in one room, somewhere on the floor.
    const room = shuffle(rooms.slice())[0]!;
    const spot = shuffle(freeTiles(map, room))[0];
    if (spot) out.push(spawnCivilian(world, spot.x, spot.y, roleFor(room.kind)));
    return out;
  }

  for (const room of rooms) {
    const area = (room.x1 - room.x0 - 1) * (room.y1 - room.y0 - 1);
    // At least one person per staffed room by day — a room nobody works in
    // isn't staffed — then scaled by area and capped so no space becomes a mob.
    const want = Math.min(
      CONFIG.civilian.maxPerRoom,
      Math.max(1, Math.round(area * CONFIG.civilian.perFloorTile * density)),
    );
    const spots = shuffle(freeTiles(map, room));
    // Prefer seats beside furniture; fall back to open floor when a room runs out.
    spots.sort((a, b) => Number(besideFurniture(map, b.x, b.y)) - Number(besideFurniture(map, a.x, a.y)));
    for (const spot of spots.slice(0, want)) {
      out.push(spawnCivilian(world, spot.x, spot.y, roleFor(room.kind)));
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Routines — the schedule state machine that produces the building's traffic.
// ---------------------------------------------------------------------------

/** What a civilian did this tick, for the caller to log/react to. */
export type CivilianEvent =
  | { kind: 'arrived'; where: string }
  | { kind: 'screamed' }   // spotted the intruder — every guard in earshot hears
  | { kind: 'reported' }   // reached a guard and told them where you were
  | null;

/** The activity a civilian is currently on. */
export function currentActivity(c: Civilian): Activity {
  return c.routine[c.step % c.routine.length]!;
}

/** A tile inside `room` someone can stand on, preferring a seat by furniture.
 *  Deterministic: the same room always yields the same anchor, so a routine
 *  reads as a person going to THEIR chair rather than teleport-shuffling. */
function anchorIn(map: MapData, room: Room): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bestScore = -Infinity;
  const cx = (room.x0 + room.x1) / 2;
  const cy = (room.y0 + room.y1) / 2;
  for (let y = room.y0 + 1; y <= room.y1 - 1; y++) {
    for (let x = room.x0 + 1; x <= room.x1 - 1; x++) {
      if (tileAt(map, x, y) !== TileType.Floor) continue;
      // Near furniture beats open floor; ties break toward the room's middle.
      const score = (besideFurniture(map, x, y) ? 100 : 0) - Math.abs(x - cx) - Math.abs(y - cy);
      if (score > bestScore) { bestScore = score; best = { x, y }; }
    }
  }
  return best;
}

/** Where the current activity happens: their own desk, or the nearest room of
 *  the requested kind. Null when the floor has no such room. */
export function activityAnchor(c: Civilian, map: MapData): { x: number; y: number } | null {
  const act = currentActivity(c);
  if (act.where === 'desk') return { x: c.homeX, y: c.homeY };
  let best: Room | null = null;
  let bestD = Infinity;
  for (const r of map.rooms) {
    if (r.kind !== act.where) continue;
    const d = Math.abs((r.x0 + r.x1) / 2 - c.x) + Math.abs((r.y0 + r.y1) / 2 - c.y);
    if (d < bestD) { bestD = d; best = r; }
  }
  return best ? anchorIn(map, best) : null;
}

/**
 * Turn to face the prop being used. NPC prop-use, in the cheapest honest form:
 * someone at a desk faces the desk, someone at the counter faces the counter —
 * so a watching player can read what a room's occupants are doing, and (with
 * their back turned) which way they aren't looking.
 */
function faceProp(c: Civilian, map: MapData): void {
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    const t = tileAt(map, c.x + dx, c.y + dy);
    if (t === TileType.PropLow || t === TileType.PropTall) {
      c.facingDx = dx;
      c.facingDy = dy;
      return;
    }
  }
}

/** Move to the next stop on the loop and drop the current target. */
function nextStep(c: Civilian): void {
  c.step = (c.step + 1) % c.routine.length;
  c.targetX = null;
  c.targetY = null;
  c.dwell = 0;
  c.path = null;
}

/**
 * Advance one civilian a tick: dwell at the current stop, then walk to the next
 * one. Walking reuses the guard mover (A*, doors, step-locked interpolation), so
 * civilians route exactly like every other NPC — which is what makes their
 * traffic legible to a player watching a corridor.
 *
 * A stop whose room doesn't exist on this floor, or that can't be reached inside
 * `reachTimeoutTicks`, is skipped rather than retried forever.
 */
export function updateCivilian(
  c: Civilian,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  threat?: {
    player: Player;
    guards: readonly Guard[];
    /** Emit a sound into the flood — the scream. */
    emit: (x: number, y: number, loudness: number) => void;
  },
): CivilianEvent {
  if (c.body.dead) { c.fovTiles = null; syncVisual(c); return null; }

  // Finish an in-progress step (same step-locked model as guards).
  if (c.moveProgress > 0) {
    const diag = c.moveDx !== 0 && c.moveDy !== 0;
    const rate = guardSpeed(c) * (diag ? 1 / Math.SQRT2 : 1);
    c.moveProgress += rate * tickDt;
    if (c.moveProgress >= 1) {
      c.x += c.moveDx;
      c.y += c.moveDy;
      c.moveProgress = 0;
      c.moveDx = 0;
      c.moveDy = 0;
      if (c.path && c.path.idx < c.path.steps.length - 1) c.path.idx++;
    }
    syncVisual(c);
    return null;
  }

  // Panic overrides the day job entirely — but only once the current step has
  // landed, or a fleeing worker would freeze mid-stride between two tiles.
  if (threat) {
    const panicEvent = updatePanic(c, map, occupied, tickDt, threat);
    if (panicEvent !== undefined) { syncVisual(c); return panicEvent; }
  }

  // Sitting/standing at the current stop.
  if (c.dwell > 0) {
    c.dwell -= tickDt;
    if (c.dwell <= 0) nextStep(c);
    syncVisual(c);
    return null;
  }

  // Need somewhere to be.
  if (c.targetX === null || c.targetY === null) {
    const anchor = activityAnchor(c, map);
    if (!anchor) { nextStep(c); syncVisual(c); return null; } // no such room here
    c.targetX = anchor.x;
    c.targetY = anchor.y;
    c.reachTimeout = CONFIG.civilian.reachTimeoutTicks;
  }

  // Arrived → turn to whatever they came here to use, and settle in.
  if (c.x === c.targetX && c.y === c.targetY) {
    const act = currentActivity(c);
    c.dwell = act.dwellSec;
    c.targetX = null;
    c.targetY = null;
    faceProp(c, map);
    syncVisual(c);
    return { kind: 'arrived', where: act.where };
  }

  c.reachTimeout -= 1;
  if (c.reachTimeout <= 0) { nextStep(c); syncVisual(c); return null; } // give up on it
  moveTowards(c, c.targetX, c.targetY, map, occupied, tickDt, {});
  syncVisual(c);
  return null;
}

// ---------------------------------------------------------------------------
// Panic — what a civilian does about an intruder.
//
// They are not guards: they can't fight and they have no radio. What they DO
// have is a voice and legs, and both are dangerous to the player. Spotting the
// intruder makes them SCREAM (a loud sound event straight into the existing
// flood — every guard in earshot investigates) and then run to the nearest
// guard to report in person. With nobody left to tell, they cower where they
// are. Sight is the janitor's model: omnidirectional, light-gated, and beaten
// by crouching behind cover.
// ---------------------------------------------------------------------------

/** Can this civilian see the intruder right now? */
export function civilianSeesIntruder(c: Civilian, player: Player, map: MapData): boolean {
  if (player.body.dead || c.body.dead) return false;
  if (player.invisible) return false; // debug ghost
  if (isBlinded(c)) return false;
  if (player.concealedAt !== null) return false; // shut inside a prop
  // A uniform works on office staff far better than on security: they take a
  // plausible colleague at face value and only look twice at someone plainly
  // out of place — wrong room, running, gun out.
  if (exposure(player, map, c.x, c.y) < CONFIG.disguise.civilianTolerance) return false;
  const d = Math.max(Math.abs(c.x - player.x), Math.abs(c.y - player.y));
  if (d > CONFIG.civilian.sightRange) return false;
  if (visibilityFactor(lightLevelAt(map, player.x, player.y)) <= 0) return false; // in the dark
  if (player.stance === 'crouch' && hasCoverBetween(map, c.x, c.y, player.x, player.y)) return false;
  return losClear(map, c.x, c.y, player.x, player.y);
}

/**
 * The panic branch. Returns `undefined` when the civilian isn't panicking (the
 * caller falls through to the routine), or a CivilianEvent when it handled the
 * tick. Movement is left to `moveTowards`, so a fleeing worker routes like
 * anyone else — and can be cut off.
 */
function updatePanic(
  c: Civilian,
  map: MapData,
  occupied: (x: number, y: number) => boolean,
  tickDt: number,
  threat: {
    player: Player;
    guards: readonly Guard[];
    emit: (x: number, y: number, loudness: number) => void;
  },
): CivilianEvent | undefined {
  const seen = civilianSeesIntruder(c, threat.player, map);
  if (seen) {
    c.sawX = threat.player.x;
    c.sawY = threat.player.y;
  }
  if (seen && !c.panicking) {
    // First sight: scream. Even with nobody to run to, the noise is the point —
    // it goes into the same flood the guards hear everything else through.
    c.panicking = true;
    c.cower = 0;
    c.path = null;
    c.targetX = null;
    c.targetY = null;
    c.reportGuardId = nearestLiveGuard(c, threat.guards)?.id ?? null;
    threat.emit(c.x, c.y, CONFIG.civilian.screamLoudness);
    return { kind: 'screamed' };
  }
  if (!c.panicking) return undefined;

  // Cowering: stay put until it wears off, then go back to work.
  if (c.cower > 0) {
    c.cower -= tickDt;
    if (c.cower <= 0) { c.panicking = false; c.reportGuardId = null; }
    return null;
  }

  const guard = c.reportGuardId !== null
    ? threat.guards.find((g) => g.id === c.reportGuardId && !g.body.dead)
    : undefined;
  if (!guard) {
    // Nobody left to tell — they hide their face and wait it out.
    c.cower = CONFIG.civilian.cowerSec;
    return null;
  }
  if (Math.max(Math.abs(c.x - guard.x), Math.abs(c.y - guard.y)) <= 1) {
    informGuard(guard, c.sawX, c.sawY);
    c.cower = CONFIG.civilian.cowerSec;
    return { kind: 'reported' };
  }
  moveTowards(c, guard.x, guard.y, map, occupied, tickDt, { adjacent: true });
  return null;
}

/** Bresenham line-of-sight: true if nothing opaque lies strictly between. */
function losClear(map: MapData, x0: number, y0: number, x1: number, y1: number): boolean {
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  let x = x0;
  let y = y0;
  for (;;) {
    const e2 = 2 * err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx) { err += dx; y += sy; }
    if (x === x1 && y === y1) return true;
    if (!inBounds(map, x, y)) return false;
    if (blocksVision(tileAt(map, x, y))) return false;
  }
}

function nearestLiveGuard(c: Civilian, guards: readonly Guard[]): Guard | null {
  let best: Guard | null = null;
  let bestD = Infinity;
  for (const g of guards) {
    if (g.body.dead) continue;
    const d = Math.max(Math.abs(g.x - c.x), Math.abs(g.y - c.y));
    if (d < bestD) { bestD = d; best = g; }
  }
  return best;
}
